import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { paths } from "@/config/paths";
import {
  ProtectedRoute,
  ClientRoute,
  EmployeeRoute,
} from "@/components/auth/protected-route.tsx";
import NavbarLayout from "@/components/layout/navbar/navbar-layout";
import PortalLayout from "@/components/layout/portal/portal-layout";
import { ACTIONS, RESOURCES } from "@/shared/permissions-matrix";
import { RouteErrorBoundary } from "./routes/error-boundary";

const router = createBrowserRouter([
  {
    path: paths.auth.login.path,
    lazy: async () => {
      const LoginRoute = (await import("./routes/auth/login")).default;
      return { Component: LoginRoute, errorElement: <RouteErrorBoundary /> };
    },
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.auth.register.path,
    lazy: async () => {
      const RegisterRoute = (await import("./routes/auth/register")).default;
      return { Component: RegisterRoute, errorElement: <RouteErrorBoundary /> };
    },
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.auth.forgotPassword.path,
    lazy: async () => {
      const ForgotPasswordRoute = (
        await import("./routes/auth/forgot-password.tsx")
      ).default;
      return {
        Component: ForgotPasswordRoute,
        errorElement: <RouteErrorBoundary />,
      };
    },
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.auth.resetPassword.path,
    lazy: async () => {
      const ResetPasswordRoute = (await import("./routes/auth/reset-password"))
        .default;
      return {
        Component: ResetPasswordRoute,
        errorElement: <RouteErrorBoundary />,
      };
    },
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.auth.verifyEmail.path,
    lazy: async () => {
      const VerifyEmailRoute = (await import("./routes/auth/verify-email"))
        .default;
      return {
        Component: VerifyEmailRoute,
        errorElement: <RouteErrorBoundary />,
      };
    },
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: paths.app.root.path,
    errorElement: <RouteErrorBoundary />,
    element: (
      <EmployeeRoute>
        <ProtectedRoute resource={RESOURCES.PROJECTS} action={ACTIONS.READ}>
          <NavbarLayout>
            <Outlet />
          </NavbarLayout>
        </ProtectedRoute>
      </EmployeeRoute>
    ),
    children: [
      {
        index: true,
        lazy: async () => {
          const DashboardRoute = (await import("./routes/app/dashboard/index"))
            .default;
          return {
            Component: () => (
              <ProtectedRoute
                resource={RESOURCES.PROJECTS}
                action={ACTIONS.READ}
              >
                <DashboardRoute />
              </ProtectedRoute>
            ),
            errorElement: <RouteErrorBoundary />,
          };
        },
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: paths.app.dashboard.path,
        lazy: async () => {
          const DashboardRoute = (await import("./routes/app/dashboard/index"))
            .default;
          return {
            Component: () => (
              <ProtectedRoute
                resource={RESOURCES.PROJECTS}
                action={ACTIONS.READ}
              >
                <DashboardRoute />
              </ProtectedRoute>
            ),
            errorElement: <RouteErrorBoundary />,
          };
        },
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: paths.app.projects.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const ProjectsRoute = (
                await import("./routes/app/projects/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PAYMENT_REPORTS}
                    action={ACTIONS.READ}
                  >
                    <ProjectsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.projects.detail.path,
            element: (
              <ProtectedRoute
                resource={RESOURCES.PROJECTS}
                action={ACTIONS.READ}
              >
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              {
                index: true,
                lazy: async () => {
                  const ProjectDetailRoute = (
                    await import("./routes/app/projects/[id]")
                  ).default;
                  return {
                    Component: ProjectDetailRoute,
                    errorElement: <RouteErrorBoundary />,
                  };
                },
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: ":tab",
                lazy: async () => {
                  const ProjectDetailRoute = (
                    await import("./routes/app/projects/[id]")
                  ).default;
                  return {
                    Component: ProjectDetailRoute,
                    errorElement: <RouteErrorBoundary />,
                  };
                },
                errorElement: <RouteErrorBoundary />,
              },
            ],
          },
        ],
      },
      {
        path: paths.app.invoices.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const InvoicesRoute = (
                await import("./routes/app/invoices/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PAYABLE_INVOICES}
                    action={ACTIONS.READ}
                  >
                    <InvoicesRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.invoices.detail.path,
            lazy: async () => {
              const InvoiceDetailRoute = (
                await import("./routes/app/invoices/[id]")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PAYABLE_INVOICES}
                    action={ACTIONS.READ}
                  >
                    <InvoiceDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.clientInvoices.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const ClientInvoicesRoute = (
                await import("./routes/app/client-invoices/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CLIENT_INVOICES}
                    action={ACTIONS.READ}
                  >
                    <ClientInvoicesRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.clientInvoices.detail.path,
            lazy: async () => {
              const ClientInvoiceDetailRoute = (
                await import("./routes/app/client-invoices/[id]")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CLIENT_INVOICES}
                    action={ACTIONS.READ}
                  >
                    <ClientInvoiceDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.paymentReports.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const PaymentReportsRoute = (
                await import("./routes/app/payment-reports/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CLIENT_INVOICES}
                    action={ACTIONS.READ}
                  >
                    <PaymentReportsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.paymentReports.detail.path,
            lazy: async () => {
              const PaymentReportDetailRoute = (
                await import("./routes/app/payment-reports/[id]")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CLIENT_INVOICES}
                    action={ACTIONS.READ}
                  >
                    <PaymentReportDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.contractors.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const ContractorsRoute = (
                await import("./routes/app/contractors/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CONTRACTORS}
                    action={ACTIONS.READ}
                  >
                    <ContractorsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.contractors.detail.path,
            lazy: async () => {
              const ContractorDetailRoute = (
                await import("./routes/app/contractors/[id]")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CONTRACTORS}
                    action={ACTIONS.READ}
                  >
                    <ContractorDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.users.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const UsersRoute = (await import("./routes/app/users/index"))
                .default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.USERS}
                    action={ACTIONS.READ}
                  >
                    <UsersRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.users.detail.path,
            lazy: async () => {
              const UserDetailRoute = (await import("./routes/app/users/[id]"))
                .default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.USERS}
                    action={ACTIONS.READ}
                  >
                    <UserDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.purchaseOrders.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const PurchaseOrdersRoute = (
                await import("./routes/app/purchase-orders/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PURCHASE_ORDERS}
                    action={ACTIONS.READ}
                  >
                    <PurchaseOrdersRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.purchaseOrders.create.path,
            lazy: async () => {
              const CreatePurchaseOrderRoute = (
                await import("./routes/app/purchase-orders/create.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PURCHASE_ORDERS}
                    action={ACTIONS.CREATE}
                  >
                    <CreatePurchaseOrderRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.purchaseOrders.detail.path,
            lazy: async () => {
              const PurchaseOrderDetailRoute = (
                await import("./routes/app/purchase-orders/[id].tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PURCHASE_ORDERS}
                    action={ACTIONS.READ}
                  >
                    <PurchaseOrderDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.suppliers.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const SuppliersRoute = (
                await import("./routes/app/suppliers/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.SUPPLIERS}
                    action={ACTIONS.READ}
                  >
                    <SuppliersRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.suppliers.detail.path,
            lazy: async () => {
              const SupplierDetailRoute = (
                await import("./routes/app/suppliers/[id]")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.SUPPLIERS}
                    action={ACTIONS.READ}
                  >
                    <SupplierDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.clients.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const ClientsRoute = (await import("./routes/app/clients/index"))
                .default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CLIENTS}
                    action={ACTIONS.READ}
                  >
                    <ClientsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.clients.detail.path,
            lazy: async () => {
              const ClientDetailRoute = (
                await import("./routes/app/clients/[id]")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CLIENTS}
                    action={ACTIONS.READ}
                  >
                    <ClientDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.changeOrders.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const ChangeOrdersRoute = (
                await import("./routes/app/change-orders/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CHANGE_ORDERS}
                    action={ACTIONS.READ}
                  >
                    <ChangeOrdersRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.changeOrders.detail.path,
            lazy: async () => {
              const ChangeOrderDetailRoute = (
                await import("./routes/app/change-orders/[id].tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.CHANGE_ORDERS}
                    action={ACTIONS.READ}
                  >
                    <ChangeOrderDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.milestones.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const MilestonesRoute = (
                await import("./routes/app/milestones/index")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.MILESTONES}
                    action={ACTIONS.READ}
                  >
                    <MilestonesRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.milestones.detail.path,
            lazy: async () => {
              const MilestoneDetailRoute = (
                await import("./routes/app/milestones/[id].tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.MILESTONES}
                    action={ACTIONS.READ}
                  >
                    <MilestoneDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.notifications.path,
        lazy: async () => {
          const NotificationsRoute = (
            await import("./routes/app/notifications/index")
          ).default;
          return {
            Component: () => (
              <ProtectedRoute
                resource={RESOURCES.NOTIFICATIONS}
                action={ACTIONS.READ}
              >
                <NotificationsRoute />
              </ProtectedRoute>
            ),
          };
        },
      },
      {
        path: paths.app.auditLogs.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const AuditLogsRoute = (
                await import("./routes/app/audit-logs/index.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.AUDIT_LOGS}
                    action={ACTIONS.READ}
                  >
                    <AuditLogsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.auditLogs.detail.path,
            lazy: async () => {
              const AuditLogDetailRoute = (
                await import("./routes/app/audit-logs/[id].tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.AUDIT_LOGS}
                    action={ACTIONS.READ}
                  >
                    <AuditLogDetailRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.reports.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const ReportsRoute = (
                await import("./routes/app/reports/index.tsx")
              ).default;
              return {
                Component: () => (
                <ProtectedRoute
                  resource={RESOURCES.PAYMENT_REPORTS}
                  action={ACTIONS.READ}
                >
                  <ReportsRoute />
                </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.reports.financial.path,
            lazy: async () => {
              const FinancialReportRoute = (
                await import("./routes/app/reports/financial.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PAYMENT_REPORTS}
                    action={ACTIONS.READ}
                  >
                    <FinancialReportRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.reports.projects.path,
            lazy: async () => {
              const ProjectsReportRoute = (
                await import("./routes/app/reports/projects.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PAYMENT_REPORTS}
                    action={ACTIONS.READ}
                  >
                    <ProjectsReportRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.reports.timeTracking.path,
            lazy: async () => {
              const TimeTrackingReportRoute = (
                await import("./routes/app/reports/time-tracking.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.PAYMENT_REPORTS}
                    action={ACTIONS.READ}
                  >
                    <TimeTrackingReportRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.settings.path,
        children: [
          {
            index: true,
            lazy: async () => {
              const SettingsRoute = (
                await import("./routes/app/settings/settings.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.USERS}
                    action={ACTIONS.READ}
                  >
                    <SettingsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.settings.profile.path,
            lazy: async () => {
              const ProfileRoute = (
                await import("./routes/app/settings/profile.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.USERS}
                    action={ACTIONS.READ}
                  >
                    <ProfileRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.settings.preferences.path,
            lazy: async () => {
              const PreferencesRoute = (
                await import("./routes/app/settings/preferences.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.USERS}
                    action={ACTIONS.READ}
                  >
                    <PreferencesRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.settings.company.path,
            lazy: async () => {
              const CompanySettingsRoute = (
                await import("./routes/app/settings/company.tsx")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.USERS}
                    action={ACTIONS.READ}
                  >
                    <CompanySettingsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
          {
            path: paths.app.settings.notifications.path,
            lazy: async () => {
              const NotificationSettingsRoute = (
                await import("./routes/app/settings/notifications-settings")
              ).default;
              return {
                Component: () => (
                  <ProtectedRoute
                    resource={RESOURCES.USERS}
                    action={ACTIONS.READ}
                  >
                    <NotificationSettingsRoute />
                  </ProtectedRoute>
                ),
              };
            },
          },
        ],
      },
      {
        path: paths.app.portal.magicLink.path,
        lazy: async () => {
          const MagicLinkRoute = (await import("./routes/portal/magic-link"))
            .default;
          return { Component: MagicLinkRoute };
        },
      },
    ],
  },
  {
    path: "portal",
    element: (
      <ClientRoute>
        <PortalLayout>
          <Outlet />
        </PortalLayout>
      </ClientRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        lazy: async () => {
          const PortalIndexRoute = (await import("./routes/portal/index"))
            .default;
          return {
            Component: PortalIndexRoute,
            errorElement: <RouteErrorBoundary />,
          };
        },
      },
      {
        path: "project/:projectId",
        children: [
          {
            index: true,
            lazy: async () => {
              const PortalOverviewRoute = (
                await import("./routes/portal/overview")
              ).default;
              return {
                Component: PortalOverviewRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "progress",
            lazy: async () => {
              const PortalProgressRoute = (
                await import("./routes/portal/progress")
              ).default;
              return {
                Component: PortalProgressRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "materials",
            lazy: async () => {
              const PortalMaterialsRoute = (
                await import("./routes/portal/materials")
              ).default;
              return {
                Component: PortalMaterialsRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "payments",
            lazy: async () => {
              const PortalPaymentsRoute = (
                await import("./routes/portal/payments")
              ).default;
              return {
                Component: PortalPaymentsRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "decisions",
            lazy: async () => {
              const PortalDecisionsRoute = (
                await import("./routes/portal/decisions")
              ).default;
              return {
                Component: PortalDecisionsRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "punchlists",
            lazy: async () => {
              const PortalPunchlistsRoute = (
                await import("./routes/portal/punchlists")
              ).default;
              return {
                Component: PortalPunchlistsRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "punchlists/:itemId",
            lazy: async () => {
              const PortalPunchlistDetailRoute = (
                await import("./routes/portal/punchlists/[id]")
              ).default;
              return {
                Component: PortalPunchlistDetailRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "change-orders",
            lazy: async () => {
              const PortalChangeOrdersRoute = (
                await import("./routes/portal/change-orders")
              ).default;
              return {
                Component: PortalChangeOrdersRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "change-orders/:id",
            lazy: async () => {
              const PortalChangeOrderDetailRoute = (
                await import("./routes/portal/change-orders/[id]")
              ).default;
              return {
                Component: PortalChangeOrderDetailRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "milestones/:id",
            lazy: async () => {
              const PortalMilestoneRoute = (
                await import("./routes/portal/milestones/[id]")
              ).default;
              return {
                Component: PortalMilestoneRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "invoices/:id",
            lazy: async () => {
              const PortalInvoiceRoute = (
                await import("./routes/portal/invoices/[id]")
              ).default;
              return {
                Component: PortalInvoiceRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "daily-logs/:id",
            lazy: async () => {
              const PortalDailyLogRoute = (
                await import("./routes/portal/daily-logs/[id]")
              ).default;
              return {
                Component: PortalDailyLogRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
          {
            path: "documents/:id",
            lazy: async () => {
              const PortalDocumentRoute = (
                await import("./routes/portal/documents/[id]")
              ).default;
              return {
                Component: PortalDocumentRoute,
                errorElement: <RouteErrorBoundary />,
              };
            },
          },
        ],
      },
      {
        path: "invoices",
        lazy: async () => {
          const PortalInvoicesRoute = (await import("./routes/portal/invoices"))
            .default;
          return {
            Component: PortalInvoicesRoute,
            errorElement: <RouteErrorBoundary />,
          };
        },
      },
      {
        path: "invoices/:id",
        lazy: async () => {
          const PortalInvoiceDetailRoute = (
            await import("./routes/portal/invoices/[id]")
          ).default;
          return {
            Component: PortalInvoiceDetailRoute,
            errorElement: <RouteErrorBoundary />,
          };
        },
      },
    ],
  },
  {
    path: "*",
    lazy: async () => {
      const NotFoundRoute = (await import("./routes/not-found")).default;
      return { Component: NotFoundRoute, errorElement: <RouteErrorBoundary /> };
    },
    errorElement: <RouteErrorBoundary />,
  },
]);

const AppRouter = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
